import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { useStore } from '../store/useStore';
import {
  getConcreteMixes, createConcreteMix, updateConcreteMix, deleteConcreteMix,
  getUsedInOptions,
  getPourLog,
  getFormSubmission, updateFormSubmission,
} from '../utils/api';
import MixModal from '../components/MixModal';
import UsedInMultiSelect from '../components/UsedInMultiSelect';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import styles from './ProjectConcrete.module.css';

// Concrete page — top: mix-design catalog (sorted ascending by strength),
// bottom: pour log derived from submitted daily reports. Admin/PM can add
// + edit + delete mixes and resolve flagged conflicts in the pour log.

// ── Pour-log export helpers ──────────────────────────────────────────
// Both formats operate on the CURRENTLY-FILTERED pourLog array passed
// in by the caller, so the export matches whatever the user is seeing
// on screen.

function csvEscape(cell) {
  const s = cell == null ? '' : String(cell);
  // Quote whenever the value contains a separator/quote/newline; double
  // up internal quotes per RFC 4180.
  if (/[,"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function pourFileBaseName(project, ext) {
  const proj = (project?.number || project?.name || 'project').replace(/[^\w-]+/g, '_');
  const stamp = format(new Date(), 'yyyyMMdd-HHmm');
  return `pour-log-${proj}-${stamp}.${ext}`;
}

// `triggerDownload` and `escapeHtml` are reused from the helpers
// defined further down in this file.

function pourConflictText(p) {
  if (!p.conflict) return '';
  if (p.conflict === 'unknown_mix') return `Mix "${p.mix_id || ''}" isn't in the catalog`;
  const expected = [].concat(p.canonical_used_in || []).filter(Boolean).join(' or ');
  const actual   = (p.used_in || []).join(', ');
  return `Mix designed for ${expected} — Used in ${actual}`;
}

function exportPourLogCsv(pourLog, project) {
  if (!pourLog.length) {
    toast('Nothing to export — pour log is empty.');
    return;
  }
  const headers = ['Date', 'Mix ID', 'Strength (PSI)', 'Level / Area / Pour', 'Qty (CY)', 'Used In', 'Conflict'];
  const lines = [headers.map(csvEscape).join(',')];
  for (const p of pourLog) {
    const date = p.date ? format(parseISO(p.date), 'yyyy-MM-dd') : '';
    const row = [
      date,
      p.mix_id || '',
      p.strength_psi != null ? String(p.strength_psi) : '',
      p.level_or_area || '',
      p.quantity_cy != null ? String(p.quantity_cy) : '',
      (p.used_in || []).join('; '),
      pourConflictText(p),
    ];
    lines.push(row.map(csvEscape).join(','));
  }
  // Add UTF-8 BOM so Excel opens it with the right encoding.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, pourFileBaseName(project, 'csv'));
}

function exportPourLogPdf(pourLog, project, filters) {
  if (!pourLog.length) {
    toast('Nothing to export — pour log is empty.');
    return;
  }
  // Pop a new tab with a print-formatted HTML doc, then auto-fire the
  // browser's print dialog — the user picks "Save as PDF" (or sends to
  // a real printer). No PDF library needed; the browser does the work.
  const w = window.open('', '_blank');
  if (!w) {
    toast.error('Popup blocked — allow popups to export the pour log to PDF.');
    return;
  }
  w.document.write(renderPourLogPrintHtml(pourLog, project, filters));
  w.document.close();
  w.focus();
  // Some browsers fire `load` before the script tag executes, so call
  // print() both on load and as a fallback after a short delay.
  const fire = () => { try { w.print(); } catch (_) { /* user closed window */ } };
  if (w.document.readyState === 'complete') fire();
  else w.addEventListener('load', fire, { once: true });
}

function renderPourLogPrintHtml(pourLog, project, filters) {
  // Reuses `escapeHtml` from the existing helpers below; alias kept
  // local so the inline template stays compact.
  const esc = (s) => escapeHtml(s == null ? '' : String(s));
  const projTitle = [project?.name, project?.number].filter(Boolean).join(' · ') || 'Project';
  const stamp = format(new Date(), 'PPpp');
  const filterChips = [];
  if (filters?.search)              filterChips.push(`Search: "${filters.search}"`);
  if (filters?.date_from || filters?.date_to)
    filterChips.push(`Date: ${filters.date_from || '…'} → ${filters.date_to || '…'}`);
  if (filters?.mix_id)               filterChips.push(`Mix: ${filters.mix_id}`);
  if (filters?.used_in_any?.length)  filterChips.push(`Used In: ${filters.used_in_any.join(', ')}`);
  if (filters?.qty_min || filters?.qty_max)
    filterChips.push(`Qty: ${filters.qty_min || 0}–${filters.qty_max || '∞'} CY`);
  if (filters?.strength_min)         filterChips.push(`Strength ≥ ${filters.strength_min} PSI`);

  const totalCy = pourLog.reduce((s, p) => s + (Number(p.quantity_cy) || 0), 0);

  const rows = pourLog.map((p) => `
    <tr${p.conflict ? ' class="conflict"' : ''}>
      <td>${esc(p.date ? format(parseISO(p.date), 'MMM d, yyyy') : '—')}</td>
      <td class="mono">${esc(p.mix_id || '—')}${p.strength_psi != null ? ` <span class="badge">${esc(p.strength_psi)} PSI</span>` : ''}</td>
      <td>${esc(p.level_or_area || '—')}</td>
      <td class="num">${p.quantity_cy != null ? esc(p.quantity_cy) : '—'}</td>
      <td>${(p.used_in || []).map((u) => `<span class="tag">${esc(u)}</span>`).join(' ') || '—'}${
        p.conflict ? `<div class="conflict-note">${esc(pourConflictText(p))}</div>` : ''
      }</td>
    </tr>
  `).join('');

  return `<!doctype html>
<html><head><meta charset="utf-8"><title>Pour Log — ${esc(projTitle)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; color: #111; }
  .page { padding: 32px 28px; max-width: 11in; margin: 0 auto; }
  h1 { margin: 0 0 4px; font-size: 18px; letter-spacing: 0.02em; }
  .meta { color: #555; font-size: 11px; margin-bottom: 12px; }
  .filters { font-size: 11px; color: #444; margin: 4px 0 14px; display: flex; flex-wrap: wrap; gap: 6px; }
  .filter-chip { background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 4px; padding: 2px 6px; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 10px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th, td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  th { background: #f9fafb; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: #4b5563; border-bottom: 1.5px solid #9ca3af; }
  td.num, th.num { text-align: right; font-variant-numeric: tabular-nums; }
  td.mono { font-family: ui-monospace, Menlo, Consolas, monospace; }
  .badge { display: inline-block; background: #eef2ff; color: #3730a3; border-radius: 3px; padding: 1px 5px; font-size: 9px; font-weight: 600; margin-left: 4px; }
  .tag { display: inline-block; background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 12px; padding: 1px 8px; font-size: 10px; margin: 1px 2px 1px 0; white-space: nowrap; }
  tr.conflict td { background: rgba(248, 113, 113, 0.08); }
  .conflict-note { font-size: 10px; color: #b91c1c; margin-top: 3px; }
  tfoot td { font-weight: 700; background: #f9fafb; border-top: 1.5px solid #9ca3af; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .page { padding: 0.5in; max-width: none; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
  }
</style>
</head><body><div class="page">
  <h1>Pour Log — ${esc(projTitle)}</h1>
  <div class="meta">Generated ${esc(stamp)} · ${pourLog.length} pour${pourLog.length === 1 ? '' : 's'}</div>
  ${filterChips.length ? `<div class="filters">${filterChips.map((c) => `<span class="filter-chip">${esc(c)}</span>`).join('')}</div>` : ''}
  <table>
    <thead><tr>
      <th>Date</th><th>Mix ID</th><th>Level / Area / Pour</th><th class="num">Qty (CY)</th><th>Used In</th>
    </tr></thead>
    <tbody>${rows}</tbody>
    <tfoot><tr>
      <td colspan="3">Total</td>
      <td class="num">${totalCy.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
      <td></td>
    </tr></tfoot>
  </table>
</div></body></html>`;
}

export default function ProjectConcrete() {
  const { project, role } = useStore();
  const canEdit = role === 'admin' || role === 'pm';

  // ── Mixes state ──
  const [mixes, setMixes]             = useState([]);
  const [usedInOptions, setUsedInOptions] = useState([]);
  const [mixesLoading, setMixesLoading] = useState(false);
  const [usedInFilter, setUsedInFilter] = useState(null); // string or null
  const [editingMix, setEditingMix]   = useState(null);    // 'new' | row | null
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // ── Pour log state ──
  const [pourLog, setPourLog]         = useState([]);
  const [pourLoading, setPourLoading] = useState(false);
  const [pourFilters, setPourFilters] = useState({
    search: '',
    date_from: '',
    date_to: '',
    mix_id: '',
    used_in_any: [],   // array of strings
    qty_min: '',
    qty_max: '',
    strength_min: '',
  });
  const [resolvingPour, setResolvingPour] = useState(null);

  async function refreshMixes() {
    if (!project) return;
    setMixesLoading(true);
    try { setMixes(await getConcreteMixes(project.id)); }
    catch (err) { toast.error(err.message); }
    finally { setMixesLoading(false); }
  }
  async function refreshUsedIn() {
    if (!project) return;
    try { setUsedInOptions(await getUsedInOptions(project.id)); }
    catch (_) { /* non-fatal */ }
  }
  async function refreshPourLog() {
    if (!project) return;
    setPourLoading(true);
    try {
      const params = { project_id: project.id };
      if (pourFilters.search)       params.search        = pourFilters.search;
      if (pourFilters.date_from)    params.date_from     = pourFilters.date_from;
      if (pourFilters.date_to)      params.date_to       = pourFilters.date_to;
      if (pourFilters.mix_id)       params.mix_id        = pourFilters.mix_id;
      if (pourFilters.used_in_any.length) params.used_in_any = pourFilters.used_in_any.join(',');
      if (pourFilters.qty_min !== '') params.qty_min     = pourFilters.qty_min;
      if (pourFilters.qty_max !== '') params.qty_max     = pourFilters.qty_max;
      if (pourFilters.strength_min !== '') params.strength_min = pourFilters.strength_min;
      setPourLog(await getPourLog(params));
    } catch (err) { toast.error(err.message); }
    finally { setPourLoading(false); }
  }

  useEffect(() => { refreshMixes();  /* eslint-disable-next-line */ }, [project?.id]);
  useEffect(() => { refreshUsedIn(); /* eslint-disable-next-line */ }, [project?.id]);
  useEffect(() => { refreshPourLog(); /* eslint-disable-next-line */ }, [
    project?.id,
    pourFilters.search, pourFilters.date_from, pourFilters.date_to,
    pourFilters.mix_id, pourFilters.qty_min, pourFilters.qty_max,
    pourFilters.strength_min, pourFilters.used_in_any.join(','),
  ]);

  // Mixes view: filtered by used-in chip if active, then ascending by
  // strength (server already sorts but the filter here is purely UI).
  // Each mix's used_in is a string array (TEXT[] in pg). A mix matches
  // the active filter when its array contains the picked value. We
  // also tolerate the legacy single-string shape from older rows.
  const visibleMixes = useMemo(() => {
    if (!usedInFilter) return mixes;
    return mixes.filter((m) => {
      if (Array.isArray(m.used_in)) return m.used_in.includes(usedInFilter);
      return m.used_in === usedInFilter;
    });
  }, [mixes, usedInFilter]);

  async function handleSaveMix(payload) {
    try {
      if (editingMix === 'new') {
        await createConcreteMix({ project_id: project.id, ...payload });
        toast.success(`Mix ${payload.mix_id} added`);
      } else if (editingMix) {
        await updateConcreteMix(editingMix.id, payload);
        toast.success(`Mix ${payload.mix_id} updated`);
      }
      setEditingMix(null);
      // refreshUsedIn picks up any options the server just auto-created
      // from the mix's used_in array.
      await Promise.all([refreshMixes(), refreshUsedIn(), refreshPourLog()]);
    } catch (err) { toast.error(err.message); }
  }
  async function handleDeleteMix(mix) {
    if (!window.confirm(`Delete mix "${mix.mix_id}"?`)) return;
    try {
      await deleteConcreteMix(mix.id);
      toast.success('Mix deleted');
      await Promise.all([refreshMixes(), refreshPourLog()]);
    } catch (err) { toast.error(err.message); }
  }

  // ── CSV / PDF export of the currently-visible mixes view ──
  function exportCsv() {
    const rows = visibleMixes;
    if (!rows.length) { toast.error('No rows to export'); return; }
    const header = ['Mix ID', 'Description', 'Used In', 'Slump/Spread (in)', 'Air (%)', 'W/CM', 'Strength (PSI)'];
    const csv = [header, ...rows.map((m) => [
      m.mix_id, m.description || '',
      formatUsedIn(m.used_in),
      m.slump_spread ?? '', m.air_pct ?? '', m.wcm ?? '', m.strength_psi ?? '',
    ])].map((row) => row.map(csvCell).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    triggerDownload(blob, `concrete-mixes-${nowSlug()}.csv`);
  }
  function exportPdf() {
    // Lightweight HTML→print path. The user picks "Save as PDF" in the
    // browser print dialog. Avoids pulling in a heavy PDF lib for a
    // first-class report.
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) { toast.error('Pop-up blocked — allow pop-ups to export PDF'); return; }
    const rowsHtml = visibleMixes.map((m) => `
      <tr>
        <td>${escapeHtml(m.mix_id)}</td>
        <td>${escapeHtml(m.description || '')}</td>
        <td>${escapeHtml(formatUsedIn(m.used_in))}</td>
        <td>${escapeHtml(m.slump_spread ?? '')}</td>
        <td>${escapeHtml(m.air_pct ?? '')}</td>
        <td>${escapeHtml(m.wcm ?? '')}</td>
        <td>${escapeHtml(m.strength_psi ?? '')}</td>
      </tr>
    `).join('');
    win.document.write(`
      <html><head><title>Concrete Mixes — ${escapeHtml(project.name)}</title>
      <style>
        body { font-family: Helvetica, Arial, sans-serif; padding: 20px; color: #111; }
        h1 { font-size: 16px; margin: 0 0 4px; }
        .sub { font-size: 11px; color: #666; margin: 0 0 14px; }
        table { width: 100%; border-collapse: collapse; font-size: 11px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #eee; }
      </style></head>
      <body>
        <h1>${escapeHtml(project.name)} — Concrete Mixes</h1>
        <p class="sub">${usedInFilter ? `Filtered to: ${escapeHtml(usedInFilter)}` : 'All mixes'} · ${visibleMixes.length} row${visibleMixes.length === 1 ? '' : 's'}</p>
        <table>
          <thead><tr>
            <th>Mix ID</th><th>Description</th><th>Used In</th>
            <th>Slump/Spread (in)</th><th>Air (%)</th><th>W/CM</th><th>Strength (PSI)</th>
          </tr></thead>
          <tbody>${rowsHtml}</tbody>
        </table>
        <script>setTimeout(() => window.print(), 250);</script>
      </body></html>
    `);
    win.document.close();
  }

  function clearPourFilters() {
    setPourFilters({
      search: '', date_from: '', date_to: '', mix_id: '',
      used_in_any: [], qty_min: '', qty_max: '', strength_min: '',
    });
  }

  if (!project) return null;

  return (
    <div className={styles.page}>
      {/* ── Mixes section ── */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div className={styles.sectionTitleWrap}>
            <h2 className={styles.sectionTitle}>Concrete Mixes</h2>
            <span className={styles.sectionSub}>
              {visibleMixes.length} mix{visibleMixes.length === 1 ? '' : 'es'}
              {usedInFilter && ` · filtered by "${usedInFilter}"`}
              {' · '}sorted by strength (PSI) ascending
            </span>
          </div>
          <div className={styles.sectionActions}>
            {usedInFilter && (
              <button className={styles.btnSecondary} onClick={() => setUsedInFilter(null)}>
                Reset view
              </button>
            )}
            <ActionMenu
              label="Export"
              items={[
                { label: 'Export as CSV', onClick: exportCsv },
                { label: 'Export as PDF', onClick: exportPdf },
              ]}
            />
            {canEdit && (
              <ActionMenu
                label="Add Mix"
                primary
                items={[
                  { label: 'Add a single mix…',  onClick: () => setEditingMix('new') },
                  { label: 'Bulk import CSV…',   onClick: () => setBulkImportOpen(true) },
                  { divider: true },
                  { label: 'Download CSV template', onClick: downloadMixesTemplate },
                ]}
              />
            )}
          </div>
        </header>

        <div className={styles.tableWrap}>
          {mixesLoading && <div className={styles.empty}>Loading…</div>}

          {!mixesLoading && visibleMixes.length === 0 && (
            <div className={styles.empty}>
              {mixes.length === 0
                ? (canEdit ? 'No mix designs yet — click + Add Mix to get started.' : 'No mix designs yet.')
                : `No mixes match the "${usedInFilter}" filter.`}
            </div>
          )}

          {visibleMixes.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Mix ID</th>
                  <th>Description</th>
                  <th>Used In</th>
                  <th className={styles.numCol}>Slump/Spread (in)</th>
                  <th className={styles.numCol}>Air (%)</th>
                  <th className={styles.numCol}>W/CM</th>
                  <th className={styles.numCol}>Strength (PSI)</th>
                  <th className={styles.actionsCol}></th>
                </tr>
              </thead>
              <tbody>
                {visibleMixes.map((m) => (
                  <tr key={m.id}>
                    <td className={styles.mixIdCell}>{m.mix_id}</td>
                    <td>{m.description || <span className={styles.muted}>—</span>}</td>
                    <td>
                      {Array.isArray(m.used_in) && m.used_in.length ? (
                        <div className={styles.usedInList}>
                          {m.used_in.map((u) => (
                            <button
                              key={u}
                              type="button"
                              className={styles.usedInChip}
                              onClick={() => setUsedInFilter(u)}
                              title="Filter to this Used In value"
                            >{u}</button>
                          ))}
                        </div>
                      ) : (typeof m.used_in === 'string' && m.used_in) ? (
                        // Legacy single-string row (pre-array migration).
                        <button
                          type="button"
                          className={styles.usedInChip}
                          onClick={() => setUsedInFilter(m.used_in)}
                          title="Filter to this Used In value"
                        >{m.used_in}</button>
                      ) : <span className={styles.muted}>—</span>}
                    </td>
                    <td className={styles.numCol}>{m.slump_spread ?? <span className={styles.muted}>—</span>}</td>
                    <td className={styles.numCol}>{m.air_pct ?? <span className={styles.muted}>—</span>}</td>
                    <td className={styles.numCol}>{m.wcm ?? <span className={styles.muted}>—</span>}</td>
                    <td className={`${styles.numCol} ${styles.strengthCell}`}>
                      {m.strength_psi != null ? m.strength_psi.toLocaleString() : <span className={styles.muted}>—</span>}
                    </td>
                    <td className={styles.actionsCol}>
                      {canEdit && (
                        <div className={styles.rowActions}>
                          <button className={styles.iconBtn} title="Edit" onClick={() => setEditingMix(m)}>
                            <PencilIcon />
                          </button>
                          <button className={styles.iconBtn} title="Delete" onClick={() => handleDeleteMix(m)}>
                            <TrashIcon />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* ── Pour log section ── */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <div className={styles.sectionTitleWrap}>
            <h2 className={styles.sectionTitle}>Pour Log</h2>
            <span className={styles.sectionSub}>
              Pulled from submitted Daily Reports — newest first.
              {' '}{pourLog.length} row{pourLog.length === 1 ? '' : 's'}.
            </span>
          </div>
          <div className={styles.sectionActions}>
            {hasActivePourFilters(pourFilters) && (
              <button className={styles.btnSecondary} onClick={clearPourFilters}>
                Clear filters
              </button>
            )}
            <ActionMenu
              label="Export"
              items={[
                {
                  label: 'CSV (.csv)',
                  onClick: () => exportPourLogCsv(pourLog, project),
                },
                {
                  label: 'PDF (Print)',
                  onClick: () => exportPourLogPdf(pourLog, project, pourFilters),
                },
              ]}
            />
          </div>
        </header>

        {/* Pour-log filter strip */}
        <div className={styles.pourFilters}>
          <input
            className={styles.pourSearch}
            placeholder="Search mix / level / used in…"
            value={pourFilters.search}
            onChange={(e) => setPourFilters((f) => ({ ...f, search: e.target.value }))}
          />
          <span className={styles.pourFilterGroup}>
            <label className={styles.pourFilterLabel}>From</label>
            <input
              type="date"
              className={styles.pourDate}
              value={pourFilters.date_from}
              onChange={(e) => setPourFilters((f) => ({ ...f, date_from: e.target.value }))}
            />
            <label className={styles.pourFilterLabel}>To</label>
            <input
              type="date"
              className={styles.pourDate}
              value={pourFilters.date_to}
              onChange={(e) => setPourFilters((f) => ({ ...f, date_to: e.target.value }))}
            />
          </span>
          <select
            className={styles.pourSelect}
            value={pourFilters.mix_id}
            onChange={(e) => setPourFilters((f) => ({ ...f, mix_id: e.target.value }))}
          >
            <option value="">All mixes</option>
            {mixes.map((m) => (
              <option key={m.id} value={m.mix_id}>{m.mix_id}</option>
            ))}
          </select>
          {/* Compact dropdown with checkboxes — better in a horizontal
              filter strip than the full chip-style picker. */}
          <MultiSelectDropdown
            label="Used In"
            options={usedInOptions.map((o) => o.name)}
            value={pourFilters.used_in_any}
            onChange={(arr) => setPourFilters((f) => ({ ...f, used_in_any: arr }))}
          />
          <span className={styles.pourFilterGroup}>
            <label className={styles.pourFilterLabel}>QTY</label>
            <input
              type="number" inputMode="decimal" step="0.01"
              className={styles.pourNum}
              placeholder="min"
              value={pourFilters.qty_min}
              onChange={(e) => setPourFilters((f) => ({ ...f, qty_min: e.target.value }))}
            />
            <span className={styles.pourFilterDash}>–</span>
            <input
              type="number" inputMode="decimal" step="0.01"
              className={styles.pourNum}
              placeholder="max"
              value={pourFilters.qty_max}
              onChange={(e) => setPourFilters((f) => ({ ...f, qty_max: e.target.value }))}
            />
          </span>
          <span className={styles.pourFilterGroup}>
            <label className={styles.pourFilterLabel}>Strength ≥</label>
            {/* Dropdown rather than free-typed number — distinct PSI
                values pulled from the project's mix catalog so the
                user picks from real strengths instead of guessing
                round numbers. Sorted ascending; the chosen value
                still drives the existing strength_min `>=` filter. */}
            <select
              className={styles.pourSelect}
              value={pourFilters.strength_min}
              onChange={(e) => setPourFilters((f) => ({ ...f, strength_min: e.target.value }))}
            >
              <option value="">Any PSI</option>
              {Array.from(new Set(mixes.map((m) => m.strength_psi).filter((v) => v != null)))
                .sort((a, b) => a - b)
                .map((v) => (
                  <option key={v} value={v}>{v} PSI</option>
                ))}
            </select>
          </span>
        </div>

        <div className={styles.tableWrap}>
          {pourLoading && <div className={styles.empty}>Loading…</div>}
          {!pourLoading && pourLog.length === 0 && (
            <div className={styles.empty}>
              No pours match the current filters.
            </div>
          )}
          {pourLog.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Mix ID</th>
                  <th>Level / Area / Pour</th>
                  <th className={styles.numCol}>QTY (CY)</th>
                  <th>Used In</th>
                  <th className={styles.actionsCol}></th>
                </tr>
              </thead>
              <tbody>
                {pourLog.map((p) => (
                  <tr key={`${p.submission_id}::${p.item_index}`} className={p.conflict ? styles.conflictRow : ''}>
                    <td>{p.date ? format(parseISO(p.date), 'MMM d, yyyy') : <span className={styles.muted}>—</span>}</td>
                    <td className={styles.mixIdCell}>
                      {p.mix_id || <span className={styles.muted}>—</span>}
                      {p.strength_psi != null && (
                        <span className={styles.strengthBadge}>{p.strength_psi} PSI</span>
                      )}
                    </td>
                    <td>{p.level_or_area || <span className={styles.muted}>—</span>}</td>
                    <td className={styles.numCol}>{p.quantity_cy || <span className={styles.muted}>—</span>}</td>
                    <td>
                      <div className={styles.usedInList}>
                        {p.used_in.length
                          ? p.used_in.map((u) => <span key={u} className={styles.usedInTag}>{u}</span>)
                          : <span className={styles.muted}>—</span>}
                      </div>
                      {p.conflict && (
                        <div className={styles.conflictNote}>
                          {p.conflict === 'unknown_mix'
                            ? `Mix "${p.mix_id}" isn't in the catalog`
                            : `Mix designed for ${(Array.isArray(p.canonical_used_in) ? p.canonical_used_in : [p.canonical_used_in]).join(' or ')} — Used in ${p.used_in.join(', ')}`}
                        </div>
                      )}
                    </td>
                    <td className={styles.actionsCol}>
                      {canEdit && p.conflict && (
                        <button
                          className={styles.resolveBtn}
                          onClick={() => setResolvingPour(p)}
                        >Resolve</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {editingMix && (
        <MixModal
          mix={editingMix === 'new' ? null : editingMix}
          usedInOptions={usedInOptions}
          onClose={() => setEditingMix(null)}
          onSave={handleSaveMix}
        />
      )}

      {bulkImportOpen && (
        <BulkImportModal
          projectId={project.id}
          existingOptions={usedInOptions.map((o) => o.name)}
          onClose={() => setBulkImportOpen(false)}
          onDone={async (count) => {
            setBulkImportOpen(false);
            if (count > 0) {
              toast.success(`Imported ${count} mix${count === 1 ? '' : 'es'}`);
              await Promise.all([refreshMixes(), refreshUsedIn(), refreshPourLog()]);
            }
          }}
        />
      )}

      {resolvingPour && (
        <ResolvePourModal
          row={resolvingPour}
          mixes={mixes}
          usedInOptions={usedInOptions}
          onClose={() => setResolvingPour(null)}
          onSaved={() => { setResolvingPour(null); refreshPourLog(); }}
        />
      )}
    </div>
  );
}

// ── ResolvePourModal ─────────────────────────────────────────────────────
// Loads the source submission, swaps out the offending concrete_log entry
// with the user's edited values, PATCHes the submission. The pour log
// re-derives on close.
function ResolvePourModal({ row, mixes, usedInOptions, onClose, onSaved }) {
  const [mixId, setMixId]     = useState(row.mix_id || '');
  const [usedIn, setUsedIn]   = useState(row.used_in || []);
  const [saving, setSaving]   = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const sub = await getFormSubmission(row.submission_id);
      const data = sub.data || {};
      const log  = Array.isArray(data.concrete_log) ? [...data.concrete_log] : [];
      if (!log[row.item_index]) throw new Error('source row not found');
      log[row.item_index] = {
        ...log[row.item_index],
        mix_id: mixId.trim(),
        used_in: usedIn,
      };
      // Strip the legacy member_type if present so it doesn't keep
      // confusing the conflict check.
      delete log[row.item_index].member_type;
      await updateFormSubmission(row.submission_id, {
        data: { ...data, concrete_log: log },
      });
      toast.success('Pour updated');
      onSaved();
    } catch (err) {
      toast.error(err.message);
      setSaving(false);
    }
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && !saving && onClose()}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <span>Resolve pour conflict</span>
          <button className={styles.modalCloseBtn} onClick={onClose}>×</button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.modalBlurb}>
            Edit the offending pour to match the mix catalog. Changes are
            saved back to the original Daily Report submission.
          </p>

          <label className={styles.modalLabel}>Mix ID</label>
          <select
            className={styles.modalInput}
            value={mixes.some((m) => m.mix_id === mixId) ? mixId : '__custom__'}
            onChange={(e) => {
              if (e.target.value === '__custom__') setMixId('');
              else setMixId(e.target.value);
            }}
          >
            {mixes.map((m) => (
              <option key={m.id} value={m.mix_id}>
                {m.mix_id} ({m.strength_psi} PSI · {formatUsedIn(m.used_in) || '—'})
              </option>
            ))}
            <option value="__custom__">Different mix (type below)…</option>
          </select>
          <input
            className={styles.modalInput}
            placeholder="Custom mix id"
            value={mixId}
            onChange={(e) => setMixId(e.target.value)}
          />

          <label className={styles.modalLabel}>Used In</label>
          <UsedInMultiSelect
            options={usedInOptions.map((o) => o.name)}
            value={usedIn}
            onChange={setUsedIn}
            allowCustom
          />
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────
function hasActivePourFilters(f) {
  return Boolean(
    f.search || f.date_from || f.date_to || f.mix_id ||
    f.used_in_any.length || f.qty_min !== '' || f.qty_max !== '' || f.strength_min !== ''
  );
}
// Render a mix's used_in (array OR legacy string) as a comma-joined
// human string for CSV / PDF export.
function formatUsedIn(v) {
  if (Array.isArray(v)) return v.join(', ');
  return v || '';
}

// Bulk-import template — handed to the user as a CSV file. The header
// matches what BulkImportModal expects to parse on the way back in. Used
// In is documented as `;`-separated to dodge CSV escaping; users can
// also wrap a comma-separated value in double-quotes if they prefer.
const TEMPLATE_HEADER = ['Mix ID', 'Description', 'Used In', 'Slump/Spread (in)', 'Air (%)', 'W/CM', 'Strength (PSI)'];
function downloadMixesTemplate() {
  const lines = [
    TEMPLATE_HEADER.join(','),
    // Two example rows to seed the format. The first uses semicolons;
    // the second uses a quoted comma — both parse correctly.
    '4000-A,Sample foundation/slab mix,Foundations;Slabs,4,5,0.45,4000',
    '5000-B,Sample columns mix,"Columns, Beams",6,4.5,0.42,5000',
  ];
  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv' });
  triggerDownload(blob, 'concrete-mixes-template.csv');
}

function csvCell(v) {
  const s = v == null ? '' : String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}
function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}
function nowSlug() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`;
}
function triggerDownload(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const PencilIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M11 2l3 3-8 8H3v-3l8-8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
  </svg>
);
const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
    <path d="M3 4h10M6 4V3h4v1M5 4l.5 9h5L11 4"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ── ActionMenu ──────────────────────────────────────────────────────────
// Button + chevron that opens a small popup menu. Replaces a row of
// adjacent buttons when the actions cluster naturally (Export → CSV/PDF;
// Add Mix → single / bulk import / template). Outside-click + Esc close.
function ActionMenu({ label, items, primary }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    function onDown(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function fire(fn) { setOpen(false); fn?.(); }

  return (
    <div ref={wrapRef} className={styles.actionMenuWrap}>
      <button
        type="button"
        className={primary ? styles.btnPrimary : styles.btnSecondary}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {label}
        <svg
          width="10" height="10" viewBox="0 0 10 10" fill="none"
          style={{ marginLeft: 6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
        >
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
        </svg>
      </button>
      {open && (
        <div className={styles.actionMenuPopup} role="menu">
          {items.map((it, i) => it.divider ? (
            <div key={i} className={styles.actionMenuDivider} />
          ) : (
            <button
              key={i}
              type="button"
              className={styles.actionMenuItem}
              onClick={() => fire(it.onClick)}
            >{it.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── BulkImportModal ──────────────────────────────────────────────────────
// Lets PM/Admin upload a CSV that matches the template header. We parse
// client-side, surface per-row errors before submitting, then POST each
// valid row through the existing single-create endpoint. Server keeps
// owning the unique-mix-id constraint, so a duplicate just gets a row-
// level error here without blocking the rest of the import.
//
// Heads-up panel for new used-in values: any value in the CSV that
// isn't already a project option (case-insensitive) is shown for
// review before import. Each entry has an editable "save as" field
// (so a typo can be corrected inline) and a "Skip" toggle (so a value
// can be dropped from the import without leaving the row out entirely).
// The server-side auto-upsert lands the corrected value when each mix
// row is created, so Settings stays clean.
function BulkImportModal({ projectId, existingOptions, onClose, onDone }) {
  const [file, setFile]       = useState(null);
  const [parsed, setParsed]   = useState(null);   // { rows, headerMap }
  const [parseErr, setParseErr] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress]   = useState({ done: 0, total: 0 });
  const [rowErrors, setRowErrors] = useState({}); // { rowIdx: errorMsg }
  // Map of original-CSV-value → { saveAs, skip } for each new option.
  // Keyed by lowercase-trimmed so two CSV cells that differ only in
  // case collapse to one review entry.
  const [optionMap, setOptionMap] = useState({});
  const fileInputRef = useRef(null);

  // Lowercase-set of options the project already has, so we can flag
  // anything in the CSV that's NEW. The map values keep the original
  // (canonical) casing for nice display in the row preview.
  const existingByLc = useMemo(() => {
    const m = new Map();
    for (const o of (existingOptions || [])) {
      m.set(o.toLowerCase(), o);
    }
    return m;
  }, [existingOptions]);

  function handleFile(f) {
    setFile(f);
    setParsed(null); setParseErr(null); setRowErrors({}); setOptionMap({});
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const txt = String(reader.result || '');
        const grid = parseCsv(txt);
        if (!grid.length) throw new Error('CSV is empty');
        const header = grid[0].map((c) => c.trim().toLowerCase());
        const required = ['mix id', 'strength (psi)'];
        for (const r of required) {
          if (!header.includes(r)) throw new Error(`Missing required column: "${r}"`);
        }
        const idx = (name) => header.indexOf(name.toLowerCase());
        const rows = [];
        for (let r = 1; r < grid.length; r++) {
          const row = grid[r];
          if (row.every((c) => !c || !c.trim())) continue; // skip blanks
          rows.push({
            mix_id:        cell(row, idx('mix id')),
            description:   cell(row, idx('description')),
            used_in:       splitUsedIn(cell(row, idx('used in'))),
            slump_spread:  cell(row, idx('slump/spread (in)')),
            air_pct:       cell(row, idx('air (%)')),
            wcm:           cell(row, idx('w/cm')),
            strength_psi:  cell(row, idx('strength (psi)')),
          });
        }
        // Build the new-options review map. For every used-in value
        // that doesn't already exist in the project, seed an entry
        // pre-filled with the original casing.
        const newMap = {};
        for (const r of rows) {
          for (const u of (r.used_in || [])) {
            const lc = u.toLowerCase();
            if (existingByLc.has(lc)) continue;
            // First occurrence wins for casing.
            if (!newMap[lc]) newMap[lc] = { original: u, saveAs: u, skip: false };
          }
        }
        setParsed({ rows });
        setOptionMap(newMap);
      } catch (err) {
        setParseErr(err.message);
      }
    };
    reader.readAsText(f);
  }

  // Resolve a CSV used-in value to its final form using the user's
  // review choices. If the user chose to skip, returns null (caller
  // drops the value from the row's used_in array). For values already
  // in the project options, we re-emit the canonical existing casing
  // so a CSV "foundations" doesn't fight a Settings "Foundations".
  function resolveUsedInValue(raw) {
    const lc = raw.toLowerCase();
    if (existingByLc.has(lc)) return existingByLc.get(lc);
    const entry = optionMap[lc];
    if (!entry) return raw;            // fallback shouldn't happen
    if (entry.skip) return null;
    const cleaned = (entry.saveAs || '').trim();
    return cleaned || null;
  }

  // Have we resolved every new option to either a non-empty saveAs or
  // an explicit skip? Drives the Import button's disabled state.
  const newOptionsReady = useMemo(() => {
    return Object.values(optionMap).every(
      (e) => e.skip || (e.saveAs || '').trim().length > 0
    );
  }, [optionMap]);

  async function handleImport() {
    if (!parsed) return;
    setImporting(true);
    setProgress({ done: 0, total: parsed.rows.length });
    const errs = {};
    let success = 0;
    for (let i = 0; i < parsed.rows.length; i++) {
      const r = parsed.rows[i];
      // Apply the user's review-step mapping to every used-in value
      // in this row. Values flagged as "skip" are dropped; everything
      // else gets canonicalised to the chosen casing.
      let usedIn = (r.used_in || [])
        .map(resolveUsedInValue)
        .filter((v) => v && v.length);
      // Dedupe within the row (case-insensitive) preserving first-seen
      // casing — covers the case where two cells collapsed to one
      // canonical option.
      const seen = new Set();
      usedIn = usedIn.filter((v) => {
        const lc = v.toLowerCase();
        if (seen.has(lc)) return false;
        seen.add(lc); return true;
      });
      try {
        await createConcreteMix({
          project_id: projectId,
          mix_id: (r.mix_id || '').trim(),
          description: r.description || null,
          used_in: usedIn.length ? usedIn : undefined,
          slump_spread: r.slump_spread === '' ? null : Number(r.slump_spread),
          air_pct:      r.air_pct === '' ? null : Number(r.air_pct),
          wcm:          r.wcm === '' ? null : Number(r.wcm),
          strength_psi: r.strength_psi === '' ? null : Math.round(Number(r.strength_psi)),
        });
        success++;
      } catch (err) {
        errs[i] = err.message;
      }
      setProgress({ done: i + 1, total: parsed.rows.length });
    }
    setRowErrors(errs);
    setImporting(false);
    if (Object.keys(errs).length === 0) {
      onDone(success);
    }
  }

  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && !importing && onClose()}>
      <div className={styles.modal} style={{ width: 720 }}>
        <div className={styles.modalHeader}>
          <span>Bulk import mixes</span>
          <button className={styles.modalCloseBtn} onClick={onClose} disabled={importing}>×</button>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.modalBlurb}>
            Drop a CSV with the same header as the template (download from
            the Add&nbsp;Mix menu). The "Used In" column accepts
            <code> Foundations;Slabs </code> or
            <code> "Foundations, Slabs" </code> — both parse to a list.
          </p>

          <div
            className={styles.dropzone}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files?.[0] || null)}
            />
            {file
              ? <span className={styles.dzFile}>{file.name}</span>
              : <span className={styles.dzPrompt}>Click to pick a CSV file</span>}
          </div>

          {parseErr && <div className={styles.rowError}>{parseErr}</div>}

          {parsed && (
            <>
              <div className={styles.previewLabel}>
                {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'} parsed
              </div>
              <div className={styles.previewWrap}>
                <table className={styles.previewTable}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Mix ID</th>
                      <th>Description</th>
                      <th>Used In</th>
                      <th className={styles.numCol}>Slump</th>
                      <th className={styles.numCol}>Air</th>
                      <th className={styles.numCol}>W/CM</th>
                      <th className={styles.numCol}>PSI</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((r, i) => (
                      <tr key={i} className={rowErrors[i] ? styles.previewRowErr : ''}>
                        <td>{i + 1}</td>
                        <td>{r.mix_id}</td>
                        <td>{r.description}</td>
                        <td>{(r.used_in || []).join(', ')}</td>
                        <td className={styles.numCol}>{r.slump_spread}</td>
                        <td className={styles.numCol}>{r.air_pct}</td>
                        <td className={styles.numCol}>{r.wcm}</td>
                        <td className={styles.numCol}>{r.strength_psi}</td>
                        <td className={styles.previewErrCell}>
                          {rowErrors[i] || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {Object.keys(optionMap).length > 0 && (
                <div className={styles.newOptionsPanel}>
                  <div className={styles.newOptionsHeader}>
                    New Used In options
                  </div>
                  <div className={styles.newOptionsBlurb}>
                    These values aren't in your project's options list yet.
                    They'll be added automatically when you import — fix
                    typos here, or skip a value to drop it from the import.
                  </div>
                  <table className={styles.newOptionsTable}>
                    <thead>
                      <tr>
                        <th>From CSV</th>
                        <th>Save as</th>
                        <th className={styles.newOptionsSkipCol}>Skip</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(optionMap).map(([lc, entry]) => (
                        <tr key={lc} className={entry.skip ? styles.newOptionsSkipRow : ''}>
                          <td className={styles.newOptionsOriginal}>{entry.original}</td>
                          <td>
                            <input
                              type="text"
                              className={styles.newOptionsInput}
                              value={entry.saveAs}
                              disabled={entry.skip}
                              onChange={(e) => setOptionMap((m) => ({
                                ...m,
                                [lc]: { ...m[lc], saveAs: e.target.value },
                              }))}
                            />
                          </td>
                          <td className={styles.newOptionsSkipCol}>
                            <input
                              type="checkbox"
                              checked={entry.skip}
                              onChange={(e) => setOptionMap((m) => ({
                                ...m,
                                [lc]: { ...m[lc], skip: e.target.checked },
                              }))}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {importing && (
            <div className={styles.previewLabel}>
              Importing… {progress.done} / {progress.total}
            </div>
          )}
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnSecondary} onClick={onClose} disabled={importing}>Cancel</button>
          <button
            className={styles.btnPrimary}
            onClick={handleImport}
            disabled={!parsed || importing || !newOptionsReady}
            title={!newOptionsReady ? 'Each new option needs a "save as" value or a skip toggle' : ''}
          >{importing ? 'Importing…' : `Import ${parsed?.rows.length || 0}`}</button>
        </div>
      </div>
    </div>
  );
}

// Pull a single cell (trimmed) at a header index, tolerating missing cols.
function cell(row, i) { return i < 0 ? '' : (row[i] || '').trim(); }

// "Foundations;Slabs" or "Foundations, Slabs" → ['Foundations', 'Slabs'].
// Empty → undefined so the server treats it as "leave alone" / null.
function splitUsedIn(s) {
  if (!s) return undefined;
  const parts = s.split(/[;,]/).map((p) => p.trim()).filter(Boolean);
  return parts.length ? parts : undefined;
}

// Minimal CSV parser. Handles double-quoted fields that contain commas
// or escaped quotes ("foo, bar" / "she said ""hi"""). Splits on \r\n and
// \n. Keeps things simple — not RFC4180 corner-cases like embedded
// newlines inside quoted fields, but that's vanishingly rare for our
// 7-column template.
function parseCsv(text) {
  const rows = [];
  const lines = text.replace(/﻿/g, '').split(/\r?\n/);
  for (const line of lines) {
    if (line === '' && rows.length === 0) continue; // skip leading blanks
    if (line === '' && rows.length === lines.length - 1) break; // trailing
    const out = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (inQ) {
        if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (c === '"') { inQ = false; }
        else { cur += c; }
      } else {
        if (c === '"' && cur === '') { inQ = true; }
        else if (c === ',') { out.push(cur); cur = ''; }
        else { cur += c; }
      }
    }
    out.push(cur);
    rows.push(out);
  }
  // Drop trailing all-empty rows.
  while (rows.length && rows[rows.length - 1].every((c) => !c)) rows.pop();
  return rows;
}

